import { useCallback } from 'react';
import { useStateBridge, StateBridge } from './useStateBridge';
import { User } from '@/types';

export interface UserState {
  currentUser: User | null;
  userProfile: User | null;
  userPreferences: {
    theme: 'light' | 'dark' | 'auto';
    language: string;
    notifications: {
      email: boolean;
      push: boolean;
      sms: boolean;
    };
    privacy: {
      profileVisibility: 'public' | 'friends' | 'private';
      activityVisibility: 'public' | 'friends' | 'private';
      locationSharing: boolean;
    };
  };
  userStats: {
    totalActivities: number;
    hostedActivities: number;
    joinedActivities: number;
    totalFriends: number;
    badges: string[];
    achievements: string[];
  };
  userFriends: {
    friends: User[];
    pendingRequests: User[];
    sentRequests: User[];
    blockedUsers: string[];
  };
  loading: boolean;
  error: string | null;
}

export interface UserStateBridge extends StateBridge<UserState> {
  // User-specific methods
  setCurrentUser: (user: User | null) => void;
  updateUserProfile: (updates: Partial<User>) => void;
  updateUserPreferences: (preferences: Partial<UserState['userPreferences']>) => void;
  updateUserStats: (stats: Partial<UserState['userStats']>) => void;
  addFriend: (friend: User) => void;
  removeFriend: (friendId: string) => void;
  addPendingRequest: (user: User) => void;
  removePendingRequest: (userId: string) => void;
  addSentRequest: (user: User) => void;
  removeSentRequest: (userId: string) => void;
  blockUser: (userId: string) => void;
  unblockUser: (userId: string) => void;
  addBadge: (badgeId: string) => void;
  addAchievement: (achievementId: string) => void;
  clearUserData: () => void;
}

const initialUserState: UserState = {
  currentUser: null,
  userProfile: null,
  userPreferences: {
    theme: 'light',
    language: 'en',
    notifications: {
      email: true,
      push: true,
      sms: false,
    },
    privacy: {
      profileVisibility: 'public',
      activityVisibility: 'friends',
      locationSharing: true,
    },
  },
  userStats: {
    totalActivities: 0,
    hostedActivities: 0,
    joinedActivities: 0,
    totalFriends: 0,
    badges: [],
    achievements: [],
  },
  userFriends: {
    friends: [],
    pendingRequests: [],
    sentRequests: [],
    blockedUsers: [],
  },
  loading: false,
  error: null,
};

export const useUserStateBridge = (
  options: {
    persist?: boolean;
    syncAcrossTabs?: boolean;
    debounceMs?: number;
  } = {}
): UserStateBridge => {
  const bridge = useStateBridge<UserState>({
    key: 'user-state',
    initialValue: initialUserState,
    persist: options.persist ?? true,
    syncAcrossTabs: options.syncAcrossTabs ?? true,
    debounceMs: options.debounceMs ?? 100,
    validate: (state) => {
      // Validate user state
      if (state.currentUser && !state.currentUser.id) {
        return false;
      }
      return true;
    },
  });

  // User-specific methods
  const setCurrentUser = useCallback(
    (user: User | null) => {
      bridge.updateState({ 
        currentUser: user,
        userProfile: user, // Set profile to current user by default
      });
    },
    [bridge]
  );

  const updateUserProfile = useCallback(
    (updates: Partial<User>) => {
      bridge.setState(prev => ({
        ...prev,
        currentUser: prev.currentUser ? { ...prev.currentUser, ...updates } : null,
        userProfile: prev.userProfile ? { ...prev.userProfile, ...updates } : null,
      }));
    },
    [bridge]
  );

  const updateUserPreferences = useCallback(
    (preferences: Partial<UserState['userPreferences']>) => {
      bridge.setState(prev => ({
        ...prev,
        userPreferences: { ...prev.userPreferences, ...preferences },
      }));
    },
    [bridge]
  );

  const updateUserStats = useCallback(
    (stats: Partial<UserState['userStats']>) => {
      bridge.setState(prev => ({
        ...prev,
        userStats: { ...prev.userStats, ...stats },
      }));
    },
    [bridge]
  );

  const addFriend = useCallback(
    (friend: User) => {
      bridge.setState(prev => ({
        ...prev,
        userFriends: {
          ...prev.userFriends,
          friends: [...prev.userFriends.friends, friend],
          pendingRequests: prev.userFriends.pendingRequests.filter(req => req.id !== friend.id),
        },
        userStats: {
          ...prev.userStats,
          totalFriends: prev.userStats.totalFriends + 1,
        },
      }));
    },
    [bridge]
  );

  const removeFriend = useCallback(
    (friendId: string) => {
      bridge.setState(prev => ({
        ...prev,
        userFriends: {
          ...prev.userFriends,
          friends: prev.userFriends.friends.filter(friend => friend.id !== friendId),
        },
        userStats: {
          ...prev.userStats,
          totalFriends: Math.max(0, prev.userStats.totalFriends - 1),
        },
      }));
    },
    [bridge]
  );

  const addPendingRequest = useCallback(
    (user: User) => {
      bridge.setState(prev => ({
        ...prev,
        userFriends: {
          ...prev.userFriends,
          pendingRequests: [...prev.userFriends.pendingRequests, user],
        },
      }));
    },
    [bridge]
  );

  const removePendingRequest = useCallback(
    (userId: string) => {
      bridge.setState(prev => ({
        ...prev,
        userFriends: {
          ...prev.userFriends,
          pendingRequests: prev.userFriends.pendingRequests.filter(req => req.id !== userId),
        },
      }));
    },
    [bridge]
  );

  const addSentRequest = useCallback(
    (user: User) => {
      bridge.setState(prev => ({
        ...prev,
        userFriends: {
          ...prev.userFriends,
          sentRequests: [...prev.userFriends.sentRequests, user],
        },
      }));
    },
    [bridge]
  );

  const removeSentRequest = useCallback(
    (userId: string) => {
      bridge.setState(prev => ({
        ...prev,
        userFriends: {
          ...prev.userFriends,
          sentRequests: prev.userFriends.sentRequests.filter(req => req.id !== userId),
        },
      }));
    },
    [bridge]
  );

  const blockUser = useCallback(
    (userId: string) => {
      bridge.setState(prev => ({
        ...prev,
        userFriends: {
          ...prev.userFriends,
          blockedUsers: [...prev.userFriends.blockedUsers, userId],
          friends: prev.userFriends.friends.filter(friend => friend.id !== userId),
          pendingRequests: prev.userFriends.pendingRequests.filter(req => req.id !== userId),
          sentRequests: prev.userFriends.sentRequests.filter(req => req.id !== userId),
        },
      }));
    },
    [bridge]
  );

  const unblockUser = useCallback(
    (userId: string) => {
      bridge.setState(prev => ({
        ...prev,
        userFriends: {
          ...prev.userFriends,
          blockedUsers: prev.userFriends.blockedUsers.filter(id => id !== userId),
        },
      }));
    },
    [bridge]
  );

  const addBadge = useCallback(
    (badgeId: string) => {
      bridge.setState(prev => ({
        ...prev,
        userStats: {
          ...prev.userStats,
          badges: [...prev.userStats.badges, badgeId],
        },
      }));
    },
    [bridge]
  );

  const addAchievement = useCallback(
    (achievementId: string) => {
      bridge.setState(prev => ({
        ...prev,
        userStats: {
          ...prev.userStats,
          achievements: [...prev.userStats.achievements, achievementId],
        },
      }));
    },
    [bridge]
  );

  const clearUserData = useCallback(() => {
    bridge.reset();
  }, [bridge]);

  return {
    ...bridge,
    setCurrentUser,
    updateUserProfile,
    updateUserPreferences,
    updateUserStats,
    addFriend,
    removeFriend,
    addPendingRequest,
    removePendingRequest,
    addSentRequest,
    removeSentRequest,
    blockUser,
    unblockUser,
    addBadge,
    addAchievement,
    clearUserData,
  };
};
